// Copyright (c) 2026 PGNS LLC

import { AlertTriangle } from 'lucide-react';
import { GITHUB_URL, IS_FALLBACK } from '@/lib/env';

/**
 * Shown only in the S3 + CloudFront fallback bundle (PGNS-1310).
 *
 * The fallback ships a static snapshot of the status API alongside the SPA, so
 * every fetch succeeds and the page renders exactly as it does in production.
 * That is the danger: without this banner it would present frozen data as live
 * data. The components read `unknown` rather than `up` for the same reason —
 * see `status-worker/src/snapshot.ts`.
 *
 * It renders in the shell, above the router outlet, so it is on every route.
 */
export function FallbackBanner() {
  if (!IS_FALLBACK) return null;
  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-brand border border-brand-bronze/40 bg-brand-bronze/10 p-4 mb-6"
    >
      <AlertTriangle className="size-4 mt-0.5 shrink-0 text-brand-bronze" aria-hidden="true" />
      <div className="text-sm">
        <p className="font-medium text-foreground">Serving the static fallback page</p>
        <p className="text-muted-foreground">
          Our status infrastructure is itself affected by an outage at our hosting provider, so this
          page is a static copy with no live monitoring behind it. Component states below are
          reported as unknown because nothing is currently measuring them. Incident updates are
          published{' '}
          <a href={`${GITHUB_URL}`} className="underline hover:text-foreground">
            from our repository
          </a>{' '}
          and are current as of the build time shown in each entry.
        </p>
      </div>
    </div>
  );
}
